import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '@services/api'

type Tab = 'sales' | 'purchase'
interface Row {
  id: number
  date: string
  refNo: string
  party: string
  type?: string
  amount: number
  paid: number
  balance: number
  status: 'Paid' | 'Partial' | 'Unpaid' | 'Cancelled'
  dueDate?: string | null
  overdueDays?: number
  runningBalance?: number
  lastCollection?: string | null
  lastPayment?: string | null
}
interface Party { party: string; entries: number; amount: number; paid: number; balance: number; overdue: number; lastDate: string }

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const formatDateFull = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const statusBadge: Record<string, string> = { Paid: 'badge-full', Partial: 'badge-partial', Unpaid: 'badge-unpaid', Cancelled: 'badge-draft' }
const statusLabel: Record<string, string> = { Paid: 'Paid', Partial: 'Partial', Unpaid: 'Unpaid', Cancelled: 'Cancelled' }

export function Ledger() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: Tab = searchParams.get('tab') === 'purchase' ? 'purchase' : 'sales'
  const setTab = (t: Tab) => setSearchParams(t === 'purchase' ? { tab: 'purchase' } : {})
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState('')
  const [showParties, setShowParties] = useState(true)

  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (status) params.set('status', status)
  const qs = params.toString()
  const { data, isLoading } = useQuery({ queryKey: ['ledger', tab, qs], queryFn: () => apiClient.get<any>(`/ledger/${tab}${qs ? `?${qs}` : ''}`) })
  const rows: Row[] = data?.data?.items || []
  const summary = data?.data?.summary
  const parties: Party[] = data?.data?.parties || []
  const singleParty: string | null = data?.data?.singleParty || null
  const isSales = tab === 'sales'
  const partyLabel = isSales ? 'Store' : 'Supplier'
  const refLabel = isSales ? 'Invoice' : 'Bill'
  const refLink = (r: Row) => (isSales ? `/dashboard/sales/invoices/${r.id}` : `/dashboard/procurement/purchase-bills/${r.id}`)

  const exportCsv = () => {
    const head = ['Date', refLabel, partyLabel, 'Amount', 'Paid', 'Balance', 'Status', ...(singleParty ? ['Running Balance'] : [])]
    const lines = rows.map((r) => [r.date, r.refNo, `"${r.party.replace(/"/g, '""')}"`, r.amount, r.paid, r.balance, r.status, ...(singleParty ? [r.runningBalance ?? ''] : [])].join(','))
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${tab}-ledger${singleParty ? `-${singleParty.replace(/[^\w]+/g, '-').toLowerCase()}` : ''}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 3000)
  }

  const reset = () => { setSearch(''); setFrom(''); setTo(''); setStatus('') }

  return (
    <div>
      <div className="hdr">
        <h1>Ledger</h1>
        <p>Browse purchase and sales entries with running balance, filterable by party and date.</p>
      </div>

      <div className="subtab-toggle">
        <button type="button" className={tab === 'sales' ? 'on' : ''} onClick={() => { setTab('sales'); reset() }}>Sales Ledger</button>
        <button type="button" className={tab === 'purchase' ? 'on' : ''} onClick={() => { setTab('purchase'); reset() }}>Purchase Ledger</button>
      </div>

      <div className="toolbar">
        <input type="text" placeholder={isSales ? 'Search store name...' : 'Search supplier name...'} value={search} onChange={(e) => setSearch(e.target.value)} />
        <input type="date" placeholder="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} title="From date" />
        <input type="date" placeholder="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} title="To date" />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Payment Status</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Partial">Partially Paid</option>
          <option value="Paid">Paid</option>
        </select>
        {(search || from || to || status) && <button type="button" className="btn-ghost btn-sm" onClick={reset}>Clear</button>}
        <button type="button" className="btn-ghost btn-sm" onClick={exportCsv} disabled={rows.length === 0}>Export CSV</button>
      </div>

      {summary && (
        <div className="kpis">
          <div className="kpi"><div className="label">{isSales ? 'Invoices' : 'Bills'}</div><div className="val">{summary.entries}</div><div className="sub">{summary.parties} {isSales ? 'store' : 'supplier'}{summary.parties === 1 ? '' : 's'}{singleParty ? ` · ${singleParty}` : ''}</div></div>
          <div className="kpi"><div className="label">{isSales ? 'Sales' : 'Purchases'}</div><div className="val">{formatINR(summary.total)}</div><div className="sub">in the selected range</div></div>
          <div className="kpi"><div className="label">{isSales ? 'Collected' : 'Paid'}</div><div className="val">{formatINR(summary.paid)}</div><div className="sub">{summary.total > 0 ? `${Math.round((summary.paid / summary.total) * 100)}% of ${isSales ? 'sales' : 'purchases'}` : ''}</div></div>
          <div className="kpi"><div className="label">{isSales ? 'Receivable' : 'Payable'}</div><div className={`val ${summary.outstanding > 0 ? 'text-danger' : ''}`}>{formatINR(summary.outstanding)}</div><div className="sub">{isSales ? 'open on store ledgers' : 'owed to suppliers'}</div></div>
        </div>
      )}

      <div className={showParties && parties.length > 0 ? 'ledger-grid' : ''}>
        <div className="card">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>{isSales ? 'Sales Ledger — Store-wise' : 'Purchase Ledger — Supplier-wise'}</h2>
            <button type="button" className={`btn-ghost btn-xs ${showParties ? 'on' : ''}`} onClick={() => setShowParties((v) => !v)}>{showParties ? 'Hide' : 'Show'} party totals</button>
          </div>
          {singleParty && <div className="hint" style={{ marginTop: 4 }}>Showing {singleParty} only — running balance accumulates oldest → newest; the top row is the current position.</div>}
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>{refLabel}</th>
                <th>{partyLabel}</th>
                <th className="num">Amount</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
                {singleParty && <th className="num">Running Balance</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="text-ink-soft">Loading…</td></tr>}
              {!isLoading && rows.length === 0 && <tr><td colSpan={8} className="text-ink-soft">No entries match these filters.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td title={formatDateFull(r.date)}>{formatDate(r.date)}</td>
                  <td><Link to={refLink(r)} className="font-semibold text-primary-500 hover:underline">{r.refNo}</Link>{r.type && <span className="text-[10.5px] text-ink-soft ml-1">{r.type}</span>}</td>
                  <td>
                    <button type="button" className="text-left hover:underline" onClick={() => setSearch(r.party)} title={`Show only ${r.party}`}>{r.party}</button>
                  </td>
                  <td className="num">{formatINR(r.amount)}</td>
                  <td className="num">{formatINR(r.paid)}</td>
                  <td className={`num ${r.balance > 0 ? 'font-semibold' : ''}`}>{formatINR(r.balance)}{(r.overdueDays || 0) > 0 && <div className="text-[10.5px] text-danger font-normal">{r.overdueDays}d overdue</div>}</td>
                  {singleParty && <td className={`num font-semibold ${(r.runningBalance || 0) > 0 ? 'text-danger' : ''}`}>{formatINR(r.runningBalance || 0)}</td>}
                  <td><span className={`badge ${statusBadge[r.status]}`}>{statusLabel[r.status] || r.status}</span></td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && summary && (
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan={3}>Total ({rows.length})</td>
                  <td className="num">{formatINR(summary.total)}</td>
                  <td className="num">{formatINR(summary.paid)}</td>
                  <td className={`num ${summary.outstanding > 0 ? 'text-danger' : ''}`}>{formatINR(summary.outstanding)}</td>
                  {singleParty && <td></td>}
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {showParties && parties.length > 0 && (
          <div className="card">
            <h2>{partyLabel}-wise balances</h2>
            <div className="hint" style={{ marginTop: -4, marginBottom: 8 }}>Click a {partyLabel.toLowerCase()} to see its statement with running balance.</div>
            {parties.map((p) => (
              <button
                type="button"
                key={p.party}
                onClick={() => setSearch(search === p.party ? '' : p.party)}
                className={`w-full text-left py-[8px] border-b border-[#EEEDE7] last:border-b-0 ${search === p.party ? 'bg-primary-soft rounded px-2' : ''}`}
              >
                <div className="flex justify-between gap-2 text-[13px]">
                  <span className="font-medium truncate">{p.party}</span>
                  <span className={`font-semibold ${p.balance > 0 ? 'text-danger' : 'text-good'}`}>{formatINR(p.balance)}</span>
                </div>
                <div className="text-[11px] text-ink-soft">{p.entries} {isSales ? 'invoice' : 'bill'}{p.entries === 1 ? '' : 's'} · {formatINR(p.amount)} · last {formatDate(p.lastDate)}{p.overdue > 0 ? ` · ${formatINR(p.overdue)} overdue` : ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
